export interface Muhurta {
  name: string;
  romanianName: string;
  description: string;
  romanianDescription: string;
  isAuspicious: boolean;
  startTime: Date;
  endTime: Date;
  correlateConstellation?: string;
}

// Muhurta data based on the Wikipedia article and instaastro.com
const muhurtaData: Array<Omit<Muhurta, "startTime" | "endTime">> = [
  // Day Muhurtas (1-15)
  {
    name: "Rudra",
    romanianName: "Rudra",
    description: "The first muhurta after sunrise, associated with Lord Shiva in his fierce form. Not ideal for starting new ventures.",
    romanianDescription: "RUDRA MUHURTA. Primul Muhurta de după răsărit, asociat cu zeul Shiva în forma sa cea mai teribilă. Nu este ideal pentru începerea de noi proiecte.",
    isAuspicious: false,
    correlateConstellation: "Unknown"
  },
  {
    name: "Ahi",
    romanianName: "Ahi",
    description: "Associated with the serpent energy. Avoid starting new tasks during this period.",
    romanianDescription: "AHI MUHURTA. Asociat cu energia șarpelui. Evitați începerea de noi sarcini în această perioadă.",
    isAuspicious: false,
    correlateConstellation: "Lacerta"
  },
  {
    name: "Mitra",
    romanianName: "Mitra",
    description: "Means 'friendship', associated with peace and harmony. Perfect for teamwork and partnerships.",
    romanianDescription: "MITRA MUHURTA. Înseamnă 'prietenie', asociat cu pacea și armonia. Perfect pentru munca în echipă și parteneriate.",
    isAuspicious: true,
    correlateConstellation: "Unknown"
  },
  {
    name: "Pitṝ",
    romanianName: "Pitṝ",
    description: "Refers to 'father' or ancestors. Not favorable for making purchases or new beginnings.",
    romanianDescription: "PITṚ MUHURTA. Legat de 'tată' sau strămoși. Nu este favorabil pentru achiziții sau noi începuturi.",
    isAuspicious: false,
    correlateConstellation: "Cepheus & Casseiopeia"
  },
  {
    name: "Vasu",
    romanianName: "Vasu",
    description: "Associated with wealth and abundance. Good for financial decisions and investments.",
    romanianDescription: "VASU MUHURTA. Asociat cu bogăția și abundența. Bun pentru decizii financiare și investiții.",
    isAuspicious: true,
    correlateConstellation: "Delphinus"
  },
  {
    name: "Vārāha",
    romanianName: "Vārāha",
    description: "Dedicated to Lord Vishnu's boar incarnation. Helps remove obstacles and challenges.",
    romanianDescription: "VARĂHA MUHURTA. Dedicat avatarului Varaha (mistrețul) a zeului Vishnu. Ajută la îndepărtarea obstacolelor și a provocărilor.",
    isAuspicious: true,
    correlateConstellation: "Unknown"
  },
  {
    name: "Viśvedevā",
    romanianName: "Viśvedevā",
    description: "Dedicated to Hindu deities ruling over Uttarsada Vaishva Nakshatra. Perfect for personal growth.",
    romanianDescription: "VIŞVEDEVĂ MUHURTA. Dedicat zeităților hinduse care guvernează Uttarsada Vaishva Nakshatra. Perfect pentru activități de dezvoltare personală.",
    isAuspicious: true,
    correlateConstellation: "Unknown"
  },
  {
    name: "Vidhi",
    romanianName: "Vidhi",
    description: "Means 'creator aspect of the divine'. Best for new beginnings and creative tasks. Auspicious except on Mondays and Fridays.",
    romanianDescription: "VIDHI MUHURTA. Înseamnă 'aspectul creator al lui Dumnezeu'. Cel mai bun pentru noi începuturi și sarcini creative. Favorabil cu excepția zilelor de luni și vineri.",
    isAuspicious: true,
    correlateConstellation: "Unknown"
  },
  {
    name: "Sutamukhī",
    romanianName: "Sutamukhī",
    description: "Means 'goat-faced'. Ideal for growth-related activities.",
    romanianDescription: "SUTAMUKHĪ MUHURTA. Înseamnă 'cu față de capră'. Ideal pentru activități legate de creștere și dezvoltare.",
    isAuspicious: true,
    correlateConstellation: "Auriga"
  },
  {
    name: "Puruhūta",
    romanianName: "Puruhūta",
    description: "Means 'many offerings'. Avoid making bold decisions or signing contracts during this time.",
    romanianDescription: "PURUHŪTA MUHURTA. Înseamnă 'multe ofrande'. Evitați luarea de decizii îndrăznețe sau semnarea de contracte în această perioadă.",
    isAuspicious: false,
    correlateConstellation: "Unknown (Taurus or Orion?)"
  },
  {
    name: "Vāhinī",
    romanianName: "Vāhinī",
    description: "Symbolizes movement. Not ideal for travel or starting new activities.",
    romanianDescription: "VAHINĪ MUHURTA. Simbolizează mișcarea. Nu este ideal pentru călătorii sau începerea de noi activități.",
    isAuspicious: false,
    correlateConstellation: "Unknown (Gemini?)"
  },
  {
    name: "Naktanakarā",
    romanianName: "Naktanakarā",
    description: "Means 'nightwalker'. May bring hidden challenges or complications.",
    romanianDescription: "NAKTANAKARĀ MUHURTA. Înseamnă 'cel care umblă noaptea'. Poate aduce provocări sau complicații ascunse.",
    isAuspicious: false,
    correlateConstellation: "Unknown"
  },
  {
    name: "Varuṇa",
    romanianName: "Varuṇa",
    description: "Dedicated to the Hindu deity Varuna (god of water). Auspicious for traveling.",
    romanianDescription: "VARUṇA MUHURTA. Dedicat zeității hinduse Varuna (zeul apei). Favorabil pentru călătorii.",
    isAuspicious: true,
    correlateConstellation: "Unknown"
  },
  {
    name: "Aryaman",
    romanianName: "Aryaman",
    description: "Dedicated to Lord Aryaman, the god of hospitality. Good for making agreements and partnerships. Auspicious except on Sundays.",
    romanianDescription: "ARYAMAN MUHURTA. Dedicat lui Aryaman, zeul ospitalității. Bun pentru încheierea de acorduri și parteneriate. Favorabil cu excepția duminicilor.",
    isAuspicious: true,
    correlateConstellation: "Unknown"
  },
  {
    name: "Bhaga",
    romanianName: "Bhaga",
    description: "Means 'destiny or fortune'. Not ideal for making financial decisions.",
    romanianDescription: "BHAGA MUHURTA. Înseamnă 'destin sau noroc'. Nu este ideal pentru luarea deciziilor financiare.",
    isAuspicious: false,
    correlateConstellation: "Unknown"
  },
  // Night Muhurtas (16-30)
  {
    name: "Girīśa",
    romanianName: "Girīśa",
    description: "Associated with Lord Shiva, the 'lord of the mountain'. Good for meditation.",
    romanianDescription: "GIRĪŚA MUHURTA. Asociat cu zeul Shiva, 'stăpânul muntelui'. Bun pentru meditație.",
    isAuspicious: true,
    correlateConstellation: "Unknown"
  },
  {
    name: "Ajapāda",
    romanianName: "Ajapāda",
    description: "Means 'goat foot'. Not ideal for making important decisions or starting new activities.",
    romanianDescription: "AJAPĀDA MUHURTA. Înseamnă 'picior de capră'. Nu este ideal pentru luarea deciziilor importante sau pentru începerea de noi activități.",
    isAuspicious: false,
    correlateConstellation: "Unknown"
  },
  {
    name: "Ahir-Budhnya",
    romanianName: "Ahir-Budhnya",
    description: "Means 'serpent of the deep'. Good for research or transformative work.",
    romanianDescription: "AHIR-BUDHNYA MUHURTA. Înseamnă 'șarpele adâncurilor'. Susține cercetarea și munca de transformare.",
    isAuspicious: true,
    correlateConstellation: "Hydra"
  },
  {
    name: "Puṣya",
    romanianName: "Puṣya",
    description: "Means 'the nourisher'. Extremely auspicious for all kinds of activities.",
    romanianDescription: "PUṢYA MUHURTA. Înseamnă 'hrănitorul'. Extrem de favorabil pentru toate tipurile de activități.",
    isAuspicious: true,
    correlateConstellation: "Unknown"
  },
  {
    name: "Aśvinī",
    romanianName: "Aśvinī",
    description: "Associated with the Ashwini Kumars (two Hindu gods related to health). Favorable for healing and starting new projects.",
    romanianDescription: "AŞVINĪ MUHURTA. Asociat cu Ashwini Kumars (doi zei hinduși legați de sănătate). Favorabil pentru vindecare și începerea de noi proiecte.",
    isAuspicious: true,
    correlateConstellation: "Unknown"
  },
  {
    name: "Yama",
    romanianName: "Yama",
    description: "Dedicated to Yama, the lord of death. Avoid traveling during this time and focus on meditation.",
    romanianDescription: "YAMA MUHURTA. Dedicat lui Yama, stăpânul morții. Evitați călătoriile în această perioadă și concentrați-vă pe meditație.",
    isAuspicious: false,
    correlateConstellation: "Boötes"
  },
  {
    name: "Agni",
    romanianName: "Agni",
    description: "Dedicated to the 'lord of fire', Agnidev. Favorable for spiritual activities.",
    romanianDescription: "AGNI MUHURTA. Dedicat 'stăpânului focului', Agnidev. Favorabil pentru activități spirituale.",
    isAuspicious: true,
    correlateConstellation: "Ara"
  },
  {
    name: "Vidhātṛ",
    romanianName: "Vidhātṛ",
    description: "Means 'creator of destiny'. Lucky for planning future goals, writing and creative tasks.",
    romanianDescription: "VIDHĀTṚ MUHURTA. Înseamnă 'creatorul destinului'. Susține planificarea obiectivelor viitoare, scris și activități creative.",
    isAuspicious: true,
    correlateConstellation: "Unknown"
  },
  {
    name: "Kaṇḍa",
    romanianName: "Kaṇḍa",
    description: "Effects may vary from neutral to auspicious. Suitable for focusing on the beginning stages of projects.",
    romanianDescription: "KAṇḌA MUHURTA. Efectele pot varia de la neutre la favorabile. Susține demararea (începerea) proiectelor.",
    isAuspicious: true,
    correlateConstellation: "Corona Borealis"
  },
  {
    name: "Aditi",
    romanianName: "Aditi",
    description: "Dedicated to Aditi, 'the mother of all gods'. Perfect for growth, freedom and abundance.",
    romanianDescription: "ADITI MUHURTA. Dedicat lui Aditi, 'mama tuturor zeilor'. Susține dezvoltarea interioară, libertatea și abundența.",
    isAuspicious: true,
    correlateConstellation: "Unknown"
  },
  {
    name: "Jīva",
    romanianName: "Jīva",
    description: "Means 'life force'. Focus on health during this muhurat, such as starting a new fitness regime.",
    romanianDescription: "JĪVA MUHURTA. Înseamnă 'forța vieții'. Concentrați-vă pe sănătate în timpul acestui muhurta, de exemplu începerea unui nou regim de fitness.",
    isAuspicious: true,
    correlateConstellation: "Unknown"
  },
  {
    name: "Viṣṇu",
    romanianName: "Viṣṇu",
    description: "Dedicated to Lord Vishnu, the preserver of the universe. Ideal for making relationships and professional decisions.",
    romanianDescription: "VIṢṇU MUHURTA. Dedicat zeului Vishnu, menținătorul Universului. Potrivit pentru decizii legate de relații și carieră.",
    isAuspicious: true,
    correlateConstellation: "Hercules"
  },
  {
    name: "Dyumadgadyuti",
    romanianName: "Dyumadgadyuti",
    description: "Means 'brilliance of light'. Beneficial for honing talents and skills and performing creative tasks.",
    romanianDescription: "DYUMADGADYUTI MUHURTA. Înseamnă 'strălucirea luminii'. Benefic pentru perfecționarea talentelor și a abilităților precum și pentru sarcinile creative.",
    isAuspicious: true,
    correlateConstellation: "Lyra"
  },
  {
    name: "Brahma",
    romanianName: "Brahma",
    description: "Dedicated to Lord Brahma, the creator of the universe. Perfect for focusing on creativity and innovation.",
    romanianDescription: "BRAHMA MUHURTA. Dedicat zeului Brahma, creatorul universului. Ideal pentru creativitate și inovație.",
    isAuspicious: true,
    correlateConstellation: "Cygnus"
  },
  {
    name: "Samudra",
    romanianName: "Samudra",
    description: "Means 'vast ocean'. Ideal for focusing on long-term projects, research and planning for career goals.",
    romanianDescription: "SAMUDRA MUHURTA. Înseamnă 'ocean vast'. Ideal pentru concentrarea pe proiecte pe termen lung, cercetare și planificarea obiectivelor legate de carieră.",
    isAuspicious: true,
    correlateConstellation: "Deluge"
  }
];

// Cache for muhurtas calculations
const muhurtasCache = new Map<string, Muhurta[]>();

// Generate a cache key for sunrise/sunset combination
const generateCacheKey = (sunrise: Date, sunset: Date): string => {
  return `${sunrise.getTime()}_${sunset.getTime()}`;
};

/**
 * Calculates all 30 muhurtas for a day (15 for daytime, 15 for nighttime)
 * @param sunrise The sunrise time
 * @param sunset The sunset time
 * @param referenceDate The reference date (defaults to current date)
 * @returns Array of Muhurta objects
 */
export const calculateAllMuhurtas = (
  sunrise: Date,
  sunset: Date,
  referenceDate: Date = new Date()
): Muhurta[] => {
  // Check for cached results first
  const cacheKey = generateCacheKey(sunrise, sunset);
  const cachedResult = muhurtasCache.get(cacheKey);
  if (cachedResult) {
    return cachedResult;
  }

  const result: Muhurta[] = [];

  // Use timestamps instead of Date objects for calculations
  const sunriseTime = sunrise.getTime();
  const sunsetTime = sunset.getTime();
  const tomorrowSunriseTime = sunriseTime + 24 * 60 * 60 * 1000;

  // Day muhurtas (sunrise to sunset) - 15 equal parts
  const dayMuhurtaDuration = (sunsetTime - sunriseTime) / 15;

  // Night muhurtas (sunset to next sunrise) - 15 equal parts
  const nightMuhurtaDuration = (tomorrowSunriseTime - sunsetTime) / 15;

  // Calculate day muhurtas (sunrise to sunset)
  for (let i = 0; i < 15; i++) {
    const startTime = new Date(sunriseTime + i * dayMuhurtaDuration);
    const endTime = new Date(sunriseTime + (i + 1) * dayMuhurtaDuration);

    result.push({
      ...muhurtaData[i],
      startTime,
      endTime
    });
  }

  // Calculate night muhurtas (sunset to next sunrise)
  for (let i = 0; i < 15; i++) {
    const startTime = new Date(sunsetTime + i * nightMuhurtaDuration);
    const endTime = new Date(sunsetTime + (i + 1) * nightMuhurtaDuration);

    result.push({
      ...muhurtaData[15 + i],
      startTime,
      endTime
    });
  }

  // Store in cache
  muhurtasCache.set(cacheKey, result);

  return result;
};

/**
 * Finds the current muhurta based on the reference time
 * @param sunrise The sunrise time
 * @param sunset The sunset time
 * @param referenceDate The reference date (defaults to current date)
 * @returns The current Muhurta or null if not found
 */
export const getCurrentMuhurta = (
  sunrise: Date,
  sunset: Date,
  referenceDate: Date = new Date()
): Muhurta | null => {
  const allMuhurtas = calculateAllMuhurtas(sunrise, sunset, referenceDate);
  const now = referenceDate.getTime();

  // Find the muhurta that contains the reference time
  return allMuhurtas.find(
    muhurta =>
      now >= muhurta.startTime.getTime() &&
      now < muhurta.endTime.getTime()
  ) || null;
};

/**
 * Calculates the progress percentage of the current muhurta
 * @param muhurta The muhurta to calculate progress for
 * @param referenceDate The reference date (defaults to current date)
 * @returns Progress percentage (0-100)
 */
export const calculateMuhurtaProgress = (
  muhurta: Muhurta,
  referenceDate: Date = new Date()
): number => {
  const now = referenceDate.getTime();
  const start = muhurta.startTime.getTime();
  const end = muhurta.endTime.getTime();
  const total = end - start;
  const elapsed = now - start;

  return Math.min(Math.max(Math.floor((elapsed / total) * 100), 0), 100);
};

/**
 * Gets the next muhurta after the current one
 * @param muhurta The current muhurta
 * @param allMuhurtas All muhurtas for the day
 * @returns The next muhurta or null if not found
 */
export const getNextMuhurta = (
  muhurta: Muhurta,
  allMuhurtas: Muhurta[]
): Muhurta | null => {
  const currentIndex = allMuhurtas.findIndex(
    m => m.startTime.getTime() === muhurta.startTime.getTime()
  );

  if (currentIndex === -1 || currentIndex === allMuhurtas.length - 1) {
    return null;
  }

  return allMuhurtas[currentIndex + 1];
};

/**
 * Formats a countdown string for the next muhurta
 * @param muhurta The current muhurta
 * @param referenceDate The reference date (defaults to current date)
 * @returns Countdown string in minutes and seconds
 */
export const formatMuhurtaCountdown = (
  muhurta: Muhurta,
  referenceDate: Date = new Date()
): string => {
  const now = referenceDate.getTime();
  const end = muhurta.endTime.getTime();
  const timeLeftMs = end - now;

  if (timeLeftMs <= 0) {
    return "00:00";
  }

  const minutesLeft = Math.floor(timeLeftMs / 60000);
  const secondsLeft = Math.floor((timeLeftMs % 60000) / 1000);

  return `${minutesLeft.toString().padStart(2, '0')}:${secondsLeft.toString().padStart(2, '0')}`;
};

/**
 * Calculates the Brahma Muhurta (the 14th muhurta of the night, before sunrise)
 * @param sunrise The sunrise time
 * @param sunset The sunset time of the previous day
 * @returns Brahma Muhurta object
 */
export const calculateBrahmaMuhurta = (
  sunrise: Date,
  sunset: Date
): Muhurta => {
  // Get all muhurtas
  const allMuhurtas = calculateAllMuhurtas(sunrise, sunset);

  // Brahma Muhurta is the 14th muhurta of the night (index 28 in the full array)
  // This is the second-to-last muhurta before sunrise
  const brahmaMuhurta = allMuhurtas[28];

  return {
    ...brahmaMuhurta,
    name: "Brahma Muhurta",
    romanianName: "Brahma Muhurta",
    description: "The most auspicious time for spiritual practices, meditation, and study. Occurs approximately 1 hour and 36 minutes before sunrise.",
    romanianDescription: "Cel mai favorabil moment pentru practici spirituale, meditație și studiu. Apare cu aproximativ 1 oră și 36 de minute înainte de răsărit.",
    isAuspicious: true
  };
};

/**
 * Calculates the Abhijit Muhurta (centered around solar noon)
 * @param solarNoon The solar noon time
 * @returns Abhijit Muhurta object
 */
export const calculateAbhijitMuhurta = (
  solarNoon: Date
): Muhurta => {
  // Abhijit Muhurta is 24 minutes before and 24 minutes after solar noon (total 48 minutes)
  const startTime = new Date(solarNoon.getTime() - 24 * 60 * 1000);
  const endTime = new Date(solarNoon.getTime() + 24 * 60 * 1000);

  return {
    name: "Abhijit Muhurta",
    romanianName: "Abhijit Muhurta",
    description: "Also known as Vijay Muhurta, it is one of the most auspicious times of the day. Ideal for starting new ventures, ceremonies, and important tasks.",
    romanianDescription: "Cunoscut și ca Vijay Muhurta, este unul dintre cele mai favorabile momente ale zilei. Ideal pentru începerea de noi proiecte, ceremonii și sarcini importante.",
    isAuspicious: true,
    startTime,
    endTime,
    correlateConstellation: "Zenith"
  };
};
